/**
 * Captive-gateway query parameters for the guest portal.
 *
 * The gateway (Ruijie, MikroTik, ...) opens the portal with its own query
 * string — nas_ip, nas_mac, client_mac, login_url, ssid, url, link-login, ...
 * The portal needs those again after the guest authenticates, to hand the
 * browser back to the gateway. Two things lost them:
 *   1. Anything that reloads the portal without the original query string —
 *      a payment provider's return URL (/?cashfree_callback=...), a manual
 *      refresh after an OTP step — so the gateway login was never reached.
 *   2. The Ruijie hand-off only sent our own fixed set of fields, some of
 *      them empty (e.g. wlan_user_ip, which Ruijie's preset never supplies),
 *      and dropped the gateway's own parameters — Ruijie answered
 *      "parameter error".
 *   3. It sent the credentials as user_name / user_pwd, while a Reyee EG
 *      login interface reads them from its "Login Parameters" — username /
 *      password unless changed on the gateway (Reyee EG Cookbook V1.6, 4.9.4).
 *
 * So: keep the gateway's original parameters for this browser tab
 * (sessionStorage), send every one of them back on the Ruijie hand-off, and
 * put the credentials in the gateway's own login fields.
 */

const STORAGE_KEY = 'hotwifi.gatewayParams';
const MAX_AGE_MS = 30 * 60 * 1000; // a portal visit, not a whole stay

// Present on a URL only when a gateway redirected the browser here.
const GATEWAY_MARKERS = [
    'nas_ip', 'nasip', 'nasIp', 'nasIP', 'nas_mac', 'nasmac', 'nasMac',
    'client_mac', 'client_ip', 'login_url', 'logout_url', 'gw_address', 'gw_id',
    'wlan_user_mac', 'wlanusermac', 'wlanuserip',
    'link-login', 'link-login-only', 'MA', 'SIP', 'UI',
];

// Ours, not the gateway's — never echoed back to the gateway.
const PORTAL_KEYS = new Set([
    'branchId', 'branchid', 'bid', 'cashfree_callback', 'phonepe_callback', 'order_id',
]);

// Reyee EG "Login Parameters" defaults. The landing response carries the
// NAS's own names (gatewayLoginParams, from Edit NAS → Portal Login
// Parameters); postUrl defaults to next_url, the post-login field of Ruijie
// Cloud's built-in "Ruijie" template.
export const RUIJIE_LOGIN_PARAMS = Object.freeze({ username: 'username', password: 'password', postUrl: 'next_url' });
const FIELD_NAME = /^[A-Za-z0-9_.-]{1,64}$/;

// Set by the hand-off itself; a stale copy from the gateway is replaced.
const HANDOFF_KEYS = new Set([
    'user_name', 'user_pwd', 'wlan_user_ip', 'wlan_user_mac', 'wlan_ac_id', 'redirect',
]);

// The gateway's own endpoints — the hand-off goes TO login_url, it isn't a field of it.
const ENDPOINT_KEYS = new Set(['login_url', 'logout_url']);

const ZERO_MAC = /^(00[:-]?){5}00$/;

/**
 * Guest MAC as the rest of HotWiFi stores it (AA:BB:CC:DD:EE:FF). Nomadix
 * sends MA as bare 12-hex ("0090277881A0"); dashed or dotted forms are
 * converted too. Anything that isn't a MAC passes through unchanged.
 */
export function normalizeGuestMac(mac) {
    const s = String(mac || '').trim();
    // Already colon-separated (MikroTik, Ruijie): left exactly as the gateway sent it.
    if (/^([0-9a-f]{2}:){5}[0-9a-f]{2}$/i.test(s)) return s;
    const hex = s.replace(/[.-]/g, '');
    return /^[0-9a-f]{12}$/i.test(hex) ? hex.toUpperCase().match(/.{2}/g).join(':') : s;
}

function getStorage() {
    try { return window.sessionStorage; } catch { return null; }
}

export function hasGatewayParams(params) {
    return GATEWAY_MARKERS.some((k) => params.has(k));
}

/**
 * The parameters the portal should use: the current URL's when a gateway
 * just sent the browser here (and remember them), otherwise the remembered
 * ones from earlier in this tab with the current URL's own params on top
 * (e.g. a payment callback's order_id).
 */
export function resolveGatewayParams(search, storage = getStorage(), now = Date.now()) {
    const current = new URLSearchParams(search);

    if (hasGatewayParams(current)) {
        try {
            storage?.setItem(STORAGE_KEY, JSON.stringify({ savedAt: now, query: current.toString() }));
        } catch { /* storage full/blocked — the current URL still works */ }
        return current;
    }

    let saved = null;
    try { saved = JSON.parse(storage?.getItem(STORAGE_KEY) || 'null'); } catch { saved = null; }
    if (!saved?.query || now - saved.savedAt > MAX_AGE_MS) return current;

    const merged = new URLSearchParams(saved.query);
    for (const [k, v] of current) merged.set(k, v);
    return merged;
}

/**
 * URL that hands an authenticated guest back to a Ruijie gateway.
 *
 * Base: the gateway's own login_url when it sent one, else
 * http://<gateway>:<port>/. Query: every parameter the gateway originally
 * sent (minus our own and its endpoint URLs), then the credentials in the
 * gateway's login fields (loginParams), then the fields the hand-off always
 * sent (user_name, user_pwd, ...) for gateways set up for those.
 * Empty values are left out entirely — an empty wlan_user_ip is what made
 * Ruijie reject the request.
 */
export function buildRuijieHandoffUrl({
    params,
    loginUrl,
    gatewayIp,
    gatewayPort = '2060',
    user,
    password,
    clientIP,
    deviceMAC,
    gwId,
    redirect,
    loginParams,
    extra = {},
}) {
    const fields = { ...RUIJIE_LOGIN_PARAMS };
    for (const key of Object.keys(fields)) {
        const name = String(loginParams?.[key] || '').trim();
        if (FIELD_NAME.test(name)) fields[key] = name;
    }
    const handoffKeys = new Set([...HANDOFF_KEYS, fields.username, fields.password, fields.postUrl].filter(Boolean));

    const base = loginUrl || `http://${gatewayIp}:${gatewayPort}/`;
    const baseQuery = new URLSearchParams(base.includes('?') ? base.slice(base.indexOf('?') + 1) : '');

    // Ordered, last write wins; encoded with encodeURIComponent (%20, not
    // URLSearchParams' "+") — the same encoding the hand-off always used.
    const out = new Map();
    const put = (k, v) => {
        if (v === undefined || v === null) return;
        const value = String(v).trim();
        if (value === '') return;
        out.delete(k);
        out.set(k, value);
    };

    for (const [k, v] of new URLSearchParams(params || '')) {
        if (PORTAL_KEYS.has(k) || handoffKeys.has(k) || ENDPOINT_KEYS.has(k) || baseQuery.has(k)) continue;
        put(k, v);
    }

    put(fields.username, user);
    put(fields.password, password);
    put('user_name', user);
    put('user_pwd', password);
    put('wlan_user_ip', clientIP);
    if (deviceMAC && !ZERO_MAC.test(deviceMAC)) put('wlan_user_mac', deviceMAC);
    put('wlan_ac_id', gwId || '1');
    put('redirect', redirect);
    if (fields.postUrl) put(fields.postUrl, redirect);
    for (const [k, v] of Object.entries(extra)) put(k, v);

    const qs = [...out].map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
    if (!qs) return base;
    return `${base}${base.includes('?') ? '&' : '?'}${qs}`;
}
